import { useTheme } from "@repo/ui/components/theme-provider";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { openOverlay } from "@/app/overlays";
import { notificationStore } from "@/app/providers/notification-store";
import { panelStore } from "@/app/providers/panel-store";
import { switchAgentMode, toggleAgent } from "@/features/chat";
import type { CommandContext } from "./types";

/**
 * Builds the shared command context. Every hotkey/palette/dialog store closed
 * over here is module-level. Navigation and
 * palette open/close are dispatch-level concerns (see dispatch-command.ts),
 * not part of this context.
 */
export function useCommandContext(): CommandContext {
  const { patientId: routePatientId } = useParams({ strict: false });
  const { resolvedTheme, setTheme } = useTheme();

  return useMemo<CommandContext>(
    () => ({
      toggleTheme: () => setTheme(resolvedTheme === "light" ? "dark" : "light"),
      toggleSessionPanel: () => panelStore.send({ type: "panel.toggle", panel: "session" }),
      toggleAgent,
      switchAgentMode,
      openNotifications: () => notificationStore.send({ type: "notification.open" }),
      openRegisterPatientDialog: () => openOverlay("patient.register", {}),
      openCreateTaskDialog: () => openOverlay("task.create", {}),
      openAddConditionDialog: () => openOverlay("patient.condition", { patientId: routePatientId ?? null }),
    }),
    [resolvedTheme, setTheme, routePatientId],
  );
}
